// ./src/utils/api/sentMessagePurposes.ts
//
// ROADMAP #141 — the Sent messages report's vocabulary: a label for every purpose the server sends, the filter values
// the server accepts, and the purposes a search text matches by label. PURE — no imports — so `check-sent-messages` RUNS
// it (SentMessageService re-exports everything here).

/**
 * Every purpose the server SENDS a message for → a human label (eventsmagic-api `SENDER_PURPOSE`, less the two RECEIVED
 * ones, `contact_inbound` / `contact_form`, which the report never lists). Also the Type filter's options. ⚠ #141: it
 * held 7 of them (and `reminder`, which the server never sends — reminders are `event_reminder`), so the Type filter
 * could not pick a ticket receipt or a reminder. ▶ The API's `check-sent-messages` fails when a purpose has no label.
 */
export const MESSAGE_PURPOSE_LABELS: Record<string, string> = {
  invitation: "Invitation",
  event_update: "Event update",
  event_cancellation: "Event cancellation",
  event_reminder: "Reminder",
  rsvp_confirmation: "RSVP confirmation",
  rsvp_host_notification: "RSVP notification",
  rsvp_access: "RSVP access link",
  rsvp_pending_approval: "RSVP awaiting approval",
  rsvp_denied: "RSVP declined",
  policy_link: "Waiver link",
  contact_notification: "Contact notification",
  contact_reply: "Contact reply",
  ticket_receipt: "Ticket receipt",
  ticket_host_notification: "Ticket sale notification",
  ticket_transfer: "Ticket transfer",
  ticket_refund: "Ticket refund",
  ticket_refund_host_notification: "Ticket refund notification",
  ticket_approval_pending: "Ticket application received",
  ticket_approval_declined: "Ticket application declined",
  ticket_payment_due: "Payment due",
  waitlist_joined: "Waitlist joined",
  waitlist_offer: "Waitlist offer",
  waitlist_expired: "Waitlist offer expired",
  partner_application_received: "Sponsorship application received",
  partner_application_host_notification: "Sponsorship application notification",
  partner_application_approved: "Sponsorship approved",
  partner_application_closed: "Sponsorship closed",
  partner_access_code: "Sponsorship access code",
  partner_payment_receipt: "Sponsorship payment receipt",
  partner_payment_recorded: "Sponsorship payment recorded",
  partner_payment_refunded: "Sponsorship payment refunded",
  volunteer_application_received: "Volunteer application received",
  volunteer_application_host_notification: "Volunteer application notification",
  volunteer_application_decided: "Volunteer application decided",
  volunteer_access_code: "Volunteer access code",
  item_due_reminder: "Due date reminder",
  action_needed: "Needs attention",
  signup_receipt: "Sign-up payment receipt",
  signup_host_notification: "Sign-up payment notification",
  signup_refund: "Sign-up refund",
  signup_refund_host_notification: "Sign-up refund notification",
  inventory_low_stock: "Low stock alert",
  purchase_receipt: "Purchase receipt",
  service_request: "Service request",
  // A13.2 — ours to our own inbox (it carries no account, so no host's report lists it).
  event_report: "Event reported",
  // #230 — ours to our own inbox too (no account): the hidden search check decided an organizer page.
  organizer_search_verdict: "Search listing check",
  organizer_follow_confirm: "Follow confirmation",
  organizer_new_event: "New event to followers",
  qr_tag_public_given: "Public QR tag — choose what it shows",
  vendor_documents_request: "Vendor documents — the link",
  vendor_documents_reminder: "Vendor documents — reminder",
  // #68 — to a finder (where to bring it) or a reporter (matched / not found / stopped looking).
  lost_found_update: "Lost & found update",
  // #204 — to a donor: the thank-you (with the receipt), a receipt sent again, canceled or corrected.
  donation_update: "Gift receipt",
  // #281 — to a member: bought on the organizer page, renewed, a test purchase, or the renewal reminder.
  membership_update: "Membership update",
  supporter_access_code: "Supporter page code",
};

/**
 * #141 — the report's filters, all applied by the server (eventsmagic-api `boundary/sentMessage.ts`, which mirrors
 * ms_sender). A delivery state comes from the per-recipient counters; a channel kind from the channel's key.
 */
export const SENT_MESSAGE_DELIVERY_STATE = {
  DELIVERED: "delivered",
  PENDING: "pending",
  FAILED: "failed",
} as const;

export const SENT_MESSAGE_CHANNEL_KIND = {
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp",
} as const;

export type SentMessageDeliveryState = (typeof SENT_MESSAGE_DELIVERY_STATE)[keyof typeof SENT_MESSAGE_DELIVERY_STATE];
export type SentMessageChannelKind = (typeof SENT_MESSAGE_CHANNEL_KIND)[keyof typeof SENT_MESSAGE_CHANNEL_KIND];

/**
 * #141 — the purposes whose on-screen label contains `text` (any case). A row with no subject is TITLED by its purpose's
 * label, so the server search is handed these to find it — the labels live here, not on the server.
 */
export const purposesLabelled = (text: string): string[] => {
  const q = text.trim().toLowerCase();

  if (!q) {
    return [];
  }

  return Object.entries(MESSAGE_PURPOSE_LABELS)
    .filter(([, label]) => label.toLowerCase().includes(q))
    .map(([purpose]) => purpose);
};
